import { formatDistanceToNow } from 'date-fns'
import { Bell, CheckCircle, Info, TriangleAlert } from 'lucide-react'
import { type ComponentType, useEffect, useMemo, useState } from 'react'
import { Link, useFetcher } from '@remix-run/react'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { cn } from '#app/utils/misc.tsx'

type NotificationSeverity = 'notice' | 'warning' | 'critical'

type NotificationRecord = {
	id: number
	alertId: number
	status: string
	title: string
	body: string
	severity: NotificationSeverity
	percentageChange: number
	direction: 'up' | 'down'
	triggeredAt: string
	inventoryItem: {
		id: number
		name: string
	} | null
	vendorName: string | null
	invoiceNumber: string | null
}

type LoaderResponse = {
	notifications: NotificationRecord[]
}

const severityMeta: Record<
	NotificationSeverity,
	{
		label: string
		icon: ComponentType<{ className?: string }>
		className: string
		badgeClassName: string
	}
> = {
	notice: {
		label: 'Notice',
		icon: Info,
		className: 'border-blue-200 bg-blue-50 text-blue-800',
		badgeClassName: 'bg-blue-500 hover:bg-blue-600',
	},
	warning: {
		label: 'Warning',
		icon: TriangleAlert,
		className: 'border-amber-200 bg-amber-50 text-amber-800',
		badgeClassName: 'bg-amber-500 hover:bg-amber-600',
	},
	critical: {
		label: 'Critical',
		icon: TriangleAlert,
		className: 'border-red-200 bg-red-50 text-red-800',
		badgeClassName: 'bg-red-500 hover:bg-red-600',
	},
}

export function PriceAlertBell() {
	const [open, setOpen] = useState(false)
	const listFetcher = useFetcher<LoaderResponse>()
	const ackFetcher = useFetcher<{ success: boolean }>()

	const notifications = listFetcher.data?.notifications ?? []

	const unreadCount = useMemo(
		() =>
			notifications.filter(notification => notification.status !== 'ACKNOWLEDGED')
				.length,
		[notifications],
	)

	useEffect(() => {
		listFetcher.load('/resources/price-alert-notifications')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (ackFetcher.state === 'idle' && ackFetcher.data?.success) {
			listFetcher.load('/resources/price-alert-notifications')
		}
	}, [ackFetcher.state, ackFetcher.data, listFetcher])

	const handleOpenChange = (value: boolean) => {
		setOpen(value)
		if (value) {
			listFetcher.load('/resources/price-alert-notifications')
		}
	}

	return (
		<DropdownMenu open={open} onOpenChange={handleOpenChange}>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					className="relative mr-2 h-10 w-10 rounded-full border border-border p-0"
					aria-label="Open price alerts"
				>
					<Bell className="h-5 w-5" />
					{unreadCount > 0 ? (
						<span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
							{unreadCount > 9 ? '9+' : unreadCount}
						</span>
					) : null}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-96 p-0">
				<DropdownMenuLabel className="flex items-center justify-between px-4 py-3">
					<span className="text-sm font-semibold text-foreground">
						Price Alerts
					</span>
					{unreadCount > 0 ? (
						<span className="text-xs text-muted-foreground">
							{unreadCount} requiring attention
						</span>
					) : (
						<span className="text-xs text-muted-foreground">
							All caught up
						</span>
					)}
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<div className="max-h-96 overflow-y-auto">
					{notifications.length === 0 ? (
						<p className="px-4 py-8 text-center text-sm text-muted-foreground">
							No price alerts yet. Upload invoices to start tracking supplier
							price changes.
						</p>
					) : (
						<ul className="space-y-2 px-3 py-3">
							{notifications.map(notification => {
								const severity = severityMeta[notification.severity]
								const timeAgo = formatDistanceToNow(
									new Date(notification.triggeredAt),
									{ addSuffix: true },
								)

								return (
									<li
										key={notification.id}
										className={cn(
											'rounded-lg border p-3 text-sm shadow-sm transition',
											severity.className,
											notification.status === 'ACKNOWLEDGED'
												? 'opacity-70'
												: 'border-2',
										)}
									>
										<div className="flex items-start justify-between gap-2">
											<div className="space-y-1">
												<div className="flex items-center gap-2">
													<severity.icon className="h-4 w-4" />
													<span className="font-medium">
														{notification.title}
													</span>
												</div>
												<p className="text-xs text-muted-foreground">
													{notification.body}
												</p>
												<div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
													<span>{timeAgo}</span>
													{notification.inventoryItem ? (
														<Link
															to="/inventory"
															className="underline-offset-2 hover:underline"
														>
															View inventory
														</Link>
													) : null}
													{notification.vendorName ? (
														<span>Supplier: {notification.vendorName}</span>
													) : null}
													{notification.invoiceNumber ? (
														<span>Invoice: {notification.invoiceNumber}</span>
													) : null}
												</div>
											</div>
											{notification.status === 'ACKNOWLEDGED' ? (
												<Badge variant="secondary" className="flex items-center gap-1">
													<CheckCircle className="h-3 w-3" />
													Acknowledged
												</Badge>
											) : (
												<ackFetcher.Form
													method="post"
													action="/resources/price-alert-notifications"
												>
													<input type="hidden" name="intent" value="acknowledge" />
													<input
														type="hidden"
														name="notificationId"
														value={notification.id}
													/>
													<Button
														type="submit"
														size="sm"
														variant="secondary"
														className={cn(
															'flex items-center gap-1',
															severity.badgeClassName,
														)}
														disabled={ackFetcher.state !== 'idle'}
													>
														<CheckCircle className="h-3 w-3" />
														Mark reviewed
													</Button>
												</ackFetcher.Form>
											)}
										</div>
									</li>
								)
							})}
						</ul>
					)}
				</div>
				<DropdownMenuSeparator />
				{notifications.length > 0 ? (
					<DropdownMenuItem asChild className="cursor-pointer px-4 py-3 text-center text-sm">
						<Link to="/analytics/dashboard">View analytics</Link>
					</DropdownMenuItem>
				) : (
					<DropdownMenuItem asChild className="cursor-pointer px-4 py-3 text-center text-sm">
						<Link to="/invoices/batch">Upload invoices</Link>
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
